import React, { useEffect } from 'react';
import { atom, useRecoilValue, useSetRecoilState } from 'recoil';

import { BillingQueryParams, type NavigationScreenSiteState } from '@gitbook/app-navigation';
import { useLiveRef } from '@gitbook/util-react-hooks';

import { useNavigation } from '../../../../navigation';

export type UpgradeStatusModalState = boolean;

const siteUpgradeStatusModalAtom = atom<UpgradeStatusModalState>({
    key: 'siteUpgradeStatusAtom',
    default: false,
});

interface UpgradeStatusModalAPI {
    state: boolean;
    openUpgradeStatusModal: () => void;
    closeUpgradeStatusModal: () => void;
}

/**
 *
 * A dialog to inform users about the state of their site upgrade. Can be opened in two ways:
 * - with a `billing` query parameter in the URL, useful when using Stripe callback url's
 * - with the `openUpgradeStatusModal` function, when upgrades can happen within the app
 */
export function useUpgradeStatusModal(screen?: NavigationScreenSiteState): UpgradeStatusModalAPI {
    const setStatusModal = useSetRecoilState(siteUpgradeStatusModalAtom);
    const state = useRecoilValue(siteUpgradeStatusModalAtom);
    const { navigate } = useNavigation();

    const hasUpgradeBillingParam =
        screen?.billing !== undefined &&
        [BillingQueryParams.UpgradeSuccess].includes(screen.billing);
    const hasUpgradeBillingParamRef = useLiveRef(hasUpgradeBillingParam);

    // When screen has the right billing query parameter, open the modal.
    useEffect(() => {
        setStatusModal(hasUpgradeBillingParam);
    }, [hasUpgradeBillingParam, setStatusModal]);

    return React.useMemo<UpgradeStatusModalAPI>(
        () => ({
            state,
            openUpgradeStatusModal: () => setStatusModal(true),
            closeUpgradeStatusModal: () =>
                hasUpgradeBillingParamRef.current
                    ? navigate({
                          current: {
                              billing: undefined,
                          },
                      })
                    : setStatusModal(false),
        }),
        [state, setStatusModal, hasUpgradeBillingParamRef, navigate]
    );
}
